import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from './AuthContext';
import { Download, Building2, ArrowLeftRight, Landmark, Wallet, ChevronLeft, ChevronDown, ChevronRight } from 'lucide-react';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const fmt = v =>
  v != null && v !== '' && !isNaN(parseFloat(v)) && parseFloat(v) !== 0
    ? `₹${parseFloat(v).toLocaleString('en-IN')}` : '—';

const fmtDate = d =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function computeGroups(items) {
  const office   = (items || []).filter(it => it.section !== 'invoices' && it.payment_type !== 'vendor' && it.description);
  const internal = (items || []).filter(it => it.section === 'invoices' && it.payment_type !== 'vendor' && it.description);
  const vendor   = (items || []).filter(it => it.payment_type === 'vendor' && it.description);
  const officeT   = office.reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
  const internalT = internal.reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
  const vendorT   = vendor.reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
  return { office, internal, vendor, officeT, internalT, vendorT, grandTotal: officeT + internalT + vendorT };
}

// ── Color palettes for each expense group ──────────────────────────────────
const GROUP_STYLES = {
  office:   { border: '#10b981', bg: '#f0fdf4', text: '#065f46', icon: Building2,    label: 'Office Expense' },
  internal: { border: '#3b82f6', bg: '#eff6ff', text: '#1e40af', icon: ArrowLeftRight, label: 'Internal Transfer Invoices' },
  vendor:   { border: '#f59e0b', bg: '#fffbeb', text: '#78350f', icon: Landmark,     label: 'Direct Vendor Payments' },
};

// ── Accordion group row ─────────────────────────────────────────────────────
function GroupCard({ groupKey, items, total, isOpen, onToggle }) {
  const s = GROUP_STYLES[groupKey];
  const Icon = s.icon;
  if (!total && !items.length) return null;
  return (
    <div style={{ borderBottom: '1px solid #f1f5f9' }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 20px', border: 'none', background: isOpen ? s.bg : 'transparent',
          borderLeft: `4px solid ${s.border}`, cursor: 'pointer', textAlign: 'left',
        }}
        onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = '#f8fafc'; }}
        onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: s.bg, flexShrink: 0,
          }}>
            <Icon size={14} color={s.border} />
          </span>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: s.text }}>{s.label}</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: s.text, fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}</span>
          {isOpen ? <ChevronDown size={14} color={s.text} /> : <ChevronRight size={14} color="#94a3b8" />}
        </span>
      </button>
      {isOpen && (
        <div style={{ background: '#f8fafc', padding: '4px 0' }}>
          {items.map((it, i) => (
            <div key={it.id || i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '7px 20px 7px 52px', fontSize: 13, borderBottom: '1px solid #f1f5f9',
            }}>
              <span style={{ color: '#475569' }}>
                {it.description}
                {it.additional_comment && <span style={{ color: '#94a3b8', marginLeft: 8 }}>({it.additional_comment})</span>}
              </span>
              <span style={{ color: '#1e293b', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                {fmt(it.approximate_amount) !== '—' ? fmt(it.approximate_amount) : fmt(it.actual_amount)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Summary stat card ───────────────────────────────────────────────────────
function StatCard({ label, value, color }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #e2e8f0' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

// ── Classification row ──────────────────────────────────────────────────────
function ClassRow({ label, value, borderColor, textColor, bg }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '11px 20px', borderLeft: `4px solid ${borderColor}`,
      background: bg, borderBottom: '1px solid #f1f5f9',
    }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: textColor }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: textColor, fontVariantNumeric: 'tabular-nums' }}>{fmt(value)}</span>
    </div>
  );
}

// ── Section label header ────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div style={{ padding: '11px 20px', borderBottom: '1px solid #e2e8f0' }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</span>
    </div>
  );
}

// ── Reconciliation row ──────────────────────────────────────────────────────
function RecRow({ label, value, bold, color, bg, indent }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: indent ? '8px 20px 8px 36px' : '10px 20px',
      background: bg || '#fff', borderBottom: '1px solid #f1f5f9',
    }}>
      <span style={{ fontSize: 13, color: color || (indent ? '#64748b' : '#334155'), fontWeight: bold ? 700 : 400 }}>
        {indent ? `↳ ${label}` : label}
      </span>
      <span style={{ fontSize: 13, fontWeight: bold ? 700 : 600, color: color || '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{fmt(value)}</span>
    </div>
  );
}

// ── Previous month tab ──────────────────────────────────────────────────────
function PrevMonthTab({ prev, currMonthName, openGroup, setOpenGroup }) {
  if (!prev) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No previous month report found.</div>
  );
  const prevTd = prev.tally_data || {};
  const prevG = computeGroups(prev.line_items);
  const prevActualTotal = (prev.line_items || []).reduce((s, it) => s + (parseFloat(it.actual_amount) || 0), 0);
  const prevAmtReceived = parseFloat(prevTd.amount_received) || 0;
  const prevBaseline = prevAmtReceived || prevG.grandTotal;
  const prevCarryForward = prevActualTotal > 0 ? prevActualTotal - prevBaseline : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Breakdown card */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <SectionLabel>Expense Breakdown</SectionLabel>
        {['office','internal','vendor'].map(k => (
          <GroupCard key={k} groupKey={k}
            items={prevG[k]} total={prevG[`${k}T`] || prevG[k === 'office' ? 'officeT' : k === 'internal' ? 'internalT' : 'vendorT']}
            isOpen={openGroup === `prev-${k}`} onToggle={() => setOpenGroup(g => g === `prev-${k}` ? null : `prev-${k}`)} />
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 20px', background: '#0f172a', borderTop: '2px solid #0f172a' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#fff' }}>
            <Wallet size={15} /> Total Expected Expense
          </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>{fmt(prevG.grandTotal)}</span>
        </div>
      </div>

      {/* Classification */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <SectionLabel>Payment Classification</SectionLabel>
        <ClassRow label="Credited to Account (DP Head)" value={prevG.officeT + prevG.internalT} borderColor="#f43f5e" textColor="#9f1239" bg="#fff1f2" />
        <ClassRow label="Direct Vendor Payments" value={prevG.vendorT} borderColor="#f59e0b" textColor="#78350f" bg="#fffbeb" />
      </div>

      {/* Reconciliation */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <SectionLabel>Reconciliation</SectionLabel>
        <RecRow label="Estimated Total" value={prevG.grandTotal} bold color="#475569" bg="#f8fafc" />
        {prevTd.rent_transferred && <RecRow label="Rent Transferred (Direct)" value={prevTd.rent_transferred} />}
        {prevAmtReceived > 0 && <RecRow label="Amount Received" value={prevAmtReceived} bold color="#16a34a" bg="#f0fdf4" />}
        {prevActualTotal > 0 ? (
          <>
            <RecRow label="Actual Expense" value={prevActualTotal} bold color="#1d4ed8" bg="#eff6ff" />
            <div style={{
              display: 'flex', justifyContent: 'space-between', padding: '13px 20px',
              background: prevCarryForward > 0 ? '#fff1f2' : '#f0fdf4',
              borderTop: `2px solid ${prevCarryForward > 0 ? '#f43f5e' : '#10b981'}`,
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: prevCarryForward > 0 ? '#9f1239' : '#065f46' }}>
                {prevCarryForward > 0 ? `Extra Spent → carried to ${currMonthName}` : `Surplus → carried to ${currMonthName}`}
              </span>
              <span style={{ fontSize: 14, fontWeight: 700, color: prevCarryForward > 0 ? '#9f1239' : '#065f46', fontVariantNumeric: 'tabular-nums' }}>
                {prevCarryForward > 0 ? '+' : '−'} ₹{Math.abs(prevCarryForward).toLocaleString('en-IN')}
              </span>
            </div>
          </>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            Actual amounts not yet entered for this month.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Current month tab ───────────────────────────────────────────────────────
function CurrentMonthTab({ report, prev, openGroup, setOpenGroup }) {
  const td = report.tally_data || {};
  const curr = computeGroups(report.line_items);
  const prevTd = prev ? (prev.tally_data || {}) : {};
  const prevG = prev ? computeGroups(prev.line_items) : null;
  const prevActualTotal = prev ? (prev.line_items || []).reduce((s, it) => s + (parseFloat(it.actual_amount) || 0), 0) : 0;
  const prevAmtReceived = parseFloat(prevTd.amount_received) || 0;
  const prevBaseline = prevAmtReceived || (prevG ? prevG.grandTotal : 0);
  const prevCarryForward = (prev && prevActualTotal > 0) ? prevActualTotal - prevBaseline : 0;
  const prevMonthName = prev ? MONTHS[(prev.month || 1) - 1] : '';
  const netToRequest = (curr.officeT + curr.internalT) + prevCarryForward;

  // Breakdown: items where actual ≠ approx (only items with actual > 0)
  const carryBreakdownItems = prev ? (prev.line_items || []).filter(it => {
    const approx = parseFloat(it.approximate_amount) || 0;
    const actual = parseFloat(it.actual_amount) || 0;
    return actual > 0 && actual !== approx && it.description;
  }).map(it => ({
    description: it.description,
    approx: parseFloat(it.approximate_amount) || 0,
    actual: parseFloat(it.actual_amount) || 0,
    diff: (parseFloat(it.actual_amount) || 0) - (parseFloat(it.approximate_amount) || 0),
  })) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <StatCard label="Total expected expense" value={fmt(curr.grandTotal)} />
        <StatCard label="Amount to request (DP Head)" value={fmt(netToRequest)} color="#9f1239" />
        <StatCard label="Direct vendor payments" value={fmt(curr.vendorT)} color="#78350f" />
      </div>

      {/* Breakdown card */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <SectionLabel>Expense Breakdown — Estimated</SectionLabel>
        {[
          { k: 'office',   total: curr.officeT,   items: curr.office   },
          { k: 'internal', total: curr.internalT, items: curr.internal },
          { k: 'vendor',   total: curr.vendorT,   items: curr.vendor   },
        ].map(({ k, total, items }) => (
          <GroupCard key={k} groupKey={k} items={items} total={total}
            isOpen={openGroup === `curr-${k}`}
            onToggle={() => setOpenGroup(g => g === `curr-${k}` ? null : `curr-${k}`)} />
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 20px', background: '#0f172a', borderTop: '2px solid #0f172a' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#fff' }}>
            <Wallet size={15} /> Total Expected Expense
          </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>{fmt(curr.grandTotal)}</span>
        </div>
      </div>

      {/* Payment classification */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <SectionLabel>Payment Classification</SectionLabel>
        <ClassRow label="Credited to Account (DP Head) — base" value={curr.officeT + curr.internalT} borderColor="#f43f5e" textColor="#9f1239" bg="#fff1f2" />
        <ClassRow label="Direct Vendor Payments" value={curr.vendorT} borderColor="#f59e0b" textColor="#78350f" bg="#fffbeb" />
        <ClassRow label="Grand Total" value={curr.grandTotal} borderColor="#8b5cf6" textColor="#4c1d95" bg="#f5f3ff" />
      </div>

      {/* Amount to request */}
      {prev && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <SectionLabel>Amount to Request This Month</SectionLabel>
          <RecRow label="This Month's Office Expense" value={curr.officeT + curr.internalT} bold color="#475569" bg="#f8fafc" />
          {prevCarryForward !== 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', background: prevCarryForward > 0 ? '#fff1f2' : '#f0fdf4' }}>
                <span style={{ fontSize: 13, color: prevCarryForward > 0 ? '#9f1239' : '#065f46' }}>
                  {prevMonthName} {prevCarryForward > 0 ? 'extra spent (added to this bill)' : 'surplus (deducted from this bill)'}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: prevCarryForward > 0 ? '#9f1239' : '#065f46', fontVariantNumeric: 'tabular-nums' }}>
                  {prevCarryForward > 0 ? '+' : '−'} ₹{Math.abs(prevCarryForward).toLocaleString('en-IN')}
                </span>
              </div>
              {carryBreakdownItems.length > 0 && (
                <div style={{ background: '#fdf2f2', borderTop: '1px dashed #fecdd3', padding: '8px 20px 12px' }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                    {prevMonthName} — breakdown of difference
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead>
                      <tr style={{ color: '#64748b' }}>
                        <th style={{ textAlign: 'left', paddingBottom: 4, fontWeight: 600 }}>Item</th>
                        <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 600 }}>Estimated</th>
                        <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 600 }}>Actual</th>
                        <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 600 }}>Diff</th>
                      </tr>
                    </thead>
                    <tbody>
                      {carryBreakdownItems.map((it, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #fce7e7' }}>
                          <td style={{ padding: '4px 0', color: '#0f172a' }}>{it.description}</td>
                          <td style={{ padding: '4px 0', textAlign: 'right', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
                            {it.approx > 0 ? `₹${it.approx.toLocaleString('en-IN')}` : '—'}
                          </td>
                          <td style={{ padding: '4px 0', textAlign: 'right', color: '#1d4ed8', fontVariantNumeric: 'tabular-nums' }}>
                            ₹{it.actual.toLocaleString('en-IN')}
                          </td>
                          <td style={{ padding: '4px 0', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: it.diff > 0 ? '#9f1239' : '#065f46' }}>
                            {it.diff > 0 ? '+' : '−'}₹{Math.abs(it.diff).toLocaleString('en-IN')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          <div style={{
            display: 'flex', justifyContent: 'space-between', padding: '14px 20px',
            background: '#fff1f2', borderTop: '2px solid #f43f5e',
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#9f1239' }}>Net Amount to Request</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: '#9f1239', fontVariantNumeric: 'tabular-nums' }}>
              ₹{netToRequest.toLocaleString('en-IN')}
            </span>
          </div>
        </div>
      )}

      {/* Reconciliation (if data exists) */}
      {(td.amount_received || td.actual_expense || td.rent_transferred || td.last_month_balance || td.last_month_pending) && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <SectionLabel>Reconciliation</SectionLabel>
          {td.rent_transferred   && <RecRow label="Rent Transferred (Direct)" value={td.rent_transferred} />}
          {td.amount_received    && <RecRow label="Amount Received" value={td.amount_received} bold color="#16a34a" bg="#f0fdf4" />}
          {td.actual_expense     && <RecRow label="Actual Expense" value={td.actual_expense} bold color="#1d4ed8" bg="#eff6ff" />}
          {td.last_month_balance && <RecRow label="Balance Carried Forward" value={td.last_month_balance} bold color="#16a34a" />}
          {td.last_month_pending && <RecRow label="Pending to Settle" value={td.last_month_pending} bold color="#d97706" bg="#fffbeb" />}
        </div>
      )}

      {/* Invoices */}
      {report.invoices && report.invoices.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <SectionLabel>Attached Invoices ({report.invoices.length})</SectionLabel>
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {report.invoices.map(inv => (
              <div key={inv.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.8" style={{ flexShrink: 0 }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                </svg>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {inv.original_filename}
                  </div>
                  {inv.description && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{inv.description}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <a href={inv.file} target="_blank" rel="noopener noreferrer" style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px',
                    borderRadius: 5, border: '1px solid #e2e8f0', background: '#fff',
                    textDecoration: 'none', color: '#3b82f6', fontSize: 12, fontWeight: 600,
                  }}>View</a>
                  <a href={inv.file} download={inv.original_filename} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px',
                    borderRadius: 5, border: 'none', background: '#10b981',
                    textDecoration: 'none', color: '#fff', fontSize: 12, fontWeight: 600,
                  }}>Download</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main portal ─────────────────────────────────────────────────────────────
export default function ExpensePortal() {
  const { year: yearParam, month: monthParam } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useContext(AuthContext);

  const [monthList, setMonthList]   = useState([]);
  const [report, setReport]         = useState(null);
  const [loading, setLoading]       = useState(false);
  const [creating, setCreating]     = useState(false);
  const [activeTab, setActiveTab]   = useState('current');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openGroup, setOpenGroup]   = useState('curr-office');

  // Group months by year for sidebar
  const groupedByYear = monthList.reduce((acc, m) => {
    acc[m.year] = acc[m.year] || [];
    acc[m.year].push(m);
    return acc;
  }, {});
  const years = Object.keys(groupedByYear).sort((a, b) => b - a);

  useEffect(() => {
    axios.get('/api/expenses/portal/')
      .then(r => {
        setMonthList(r.data);
        if (!yearParam && r.data.length > 0) {
          const latest = r.data[0];
          navigate(`/expenses/${latest.year}/${latest.month}`, { replace: true });
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!yearParam || !monthParam) return;
    setLoading(true);
    setReport(null);
    axios.get(`/api/expenses/portal/${yearParam}/${monthParam}/`)
      .then(r => { setReport(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [yearParam, monthParam]);

  const selectedKey = yearParam && monthParam ? `${yearParam}-${monthParam}` : null;
  const monthName = report ? MONTHS[(report.month || 1) - 1] : (monthParam ? MONTHS[(parseInt(monthParam) || 1) - 1] : '');
  const prevMonth = report?.prev_month;
  const prevMonthName = prevMonth ? MONTHS[(prevMonth.month || 1) - 1] : null;

  const handleCreate = async () => {
    if (!yearParam || !monthParam || creating) return;
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/expenses/reports/by-month/${yearParam}/${monthParam}/init/`, {}, {
        headers: { Authorization: `Token ${token}` },
      });
      setLoading(true);
      const r = await axios.get(`/api/expenses/portal/${yearParam}/${monthParam}/`);
      setReport(r.data);
      const listRes = await axios.get('/api/expenses/portal/');
      setMonthList(listRes.data);
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to create report. Only Head of Operations can create expense reports.');
    } finally {
      setCreating(false);
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#f8fafc', fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif', overflow: 'hidden' }}>

      {/* ── Sidebar ────────────────────────────────────────────────────── */}
      <div style={{
        width: sidebarOpen ? 220 : 52, flexShrink: 0,
        background: '#fff', borderRight: '1px solid #e2e8f0',
        display: 'flex', flexDirection: 'column',
        transition: 'width 0.2s ease', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 12px', borderBottom: '1px solid #f1f5f9',
        }}>
          {sidebarOpen && (
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8' }}>
              Expense Reports
            </span>
          )}
          <button
            onClick={() => setSidebarOpen(o => !o)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#94a3b8', display: 'flex', alignItems: 'center' }}
          >
            <ChevronLeft size={14} style={{ transform: sidebarOpen ? 'none' : 'rotate(180deg)', transition: 'transform 0.2s' }} />
          </button>
        </div>

        {/* Month list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 6px' }}>
          {years.map(year => (
            <div key={year} style={{ marginBottom: 8 }}>
              {sidebarOpen && (
                <div style={{ padding: '4px 8px 2px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {year}
                </div>
              )}
              {groupedByYear[year].map(m => {
                const key = `${m.year}-${m.month}`;
                const isSelected = key === selectedKey;
                return (
                  <button
                    key={key}
                    onClick={() => navigate(`/expenses/${m.year}/${m.month}`)}
                    style={{
                      width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left',
                      padding: sidebarOpen ? '8px 10px' : '8px 0',
                      borderRadius: 8, marginBottom: 2,
                      background: isSelected ? '#ecfdf5' : 'transparent',
                      display: 'flex', flexDirection: 'column', justifyContent: 'center',
                      alignItems: sidebarOpen ? 'flex-start' : 'center',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {sidebarOpen ? (
                      <>
                        <span style={{ fontSize: 13, fontWeight: isSelected ? 600 : 400, color: isSelected ? '#065f46' : '#475569' }}>
                          {MONTHS[m.month - 1]}
                        </span>
                        {m.has_reconciliation && (
                          <span style={{ fontSize: 10, color: '#10b981', fontWeight: 600 }}>✓ reconciled</span>
                        )}
                      </>
                    ) : (
                      <span style={{ fontSize: 10, fontWeight: 600, color: isSelected ? '#065f46' : '#94a3b8', lineHeight: 1.3, textAlign: 'center' }}>
                        {MONTHS[m.month - 1].slice(0, 3)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
          {monthList.length === 0 && sidebarOpen && (
            <div style={{ padding: '12px 10px', fontSize: 12, color: '#94a3b8' }}>No reports yet.</div>
          )}
        </div>

        {/* Edit link */}
        {isLoggedIn && report && sidebarOpen && (
          <div style={{ borderTop: '1px solid #f1f5f9', padding: 12 }}>
            <a
              href={`/expenses/edit/${report.year}/${report.month}`}
              style={{
                display: 'block', textAlign: 'center', padding: '8px 12px',
                background: '#10b981', color: '#fff', borderRadius: 8,
                textDecoration: 'none', fontSize: 12, fontWeight: 600,
              }}
            >
              Edit This Month
            </a>
          </div>
        )}
      </div>

      {/* ── Main content ───────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <header style={{
          background: '#fff', borderBottom: '1px solid #e2e8f0',
          padding: '18px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', marginBottom: 3 }}>
              4Sight Operations
            </div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
              {report ? `${MONTHS[(report.month || 1) - 1]} ${report.year}` : 'Expense Reports'}
            </h1>
            {report && (
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
                {[report.name, report.department, report.branch].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {report && (
              <div style={{ fontSize: 12, color: '#94a3b8' }}>
                {fmtDate(report.start_date)} – {fmtDate(report.end_date)}
              </div>
            )}
            {report && (
              <a
                href={`/api/expenses/public/${report.share_token}/excel/`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: '#0f172a', color: '#fff', padding: '8px 16px',
                  borderRadius: 8, textDecoration: 'none', fontSize: 13, fontWeight: 600,
                }}
              >
                <Download size={13} /> Export to Excel
              </a>
            )}
          </div>
        </header>

        {/* Content area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>

          {loading && (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
          )}

          {!loading && !report && yearParam && (
            <div style={{ padding: 60, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>📋</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>
                No report for {monthName} {yearParam}
              </div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 24 }}>
                This month's expense report hasn't been created yet.
              </div>
              {isLoggedIn && (
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  style={{ padding: '10px 28px', background: creating ? '#94a3b8' : '#0f172a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: creating ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                  {creating ? 'Creating…' : `Create ${monthName} ${yearParam} Report`}
                </button>
              )}
            </div>
          )}

          {!loading && !yearParam && monthList.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
              No expense reports have been created yet.
            </div>
          )}

          {!loading && report && (
            <>
              {/* Tab switcher */}
              <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: 10, padding: 4, marginBottom: 24, border: '1px solid #e2e8f0' }}>
                {[
                  { key: 'prev', label: prevMonth ? `${prevMonthName} ${prevMonth.year}` : 'Previous Month', sub: 'Reconciliation' },
                  { key: 'current', label: `${monthName} ${report.year}`, sub: 'Current Estimates' },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      padding: '8px 18px', border: 'none', cursor: 'pointer', borderRadius: 7,
                      background: activeTab === tab.key ? '#fff' : 'transparent',
                      boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                      transition: 'all 0.15s',
                      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600, color: activeTab === tab.key ? '#0f172a' : '#64748b' }}>
                      {tab.label}
                    </span>
                    <span style={{ fontSize: 11, color: activeTab === tab.key ? '#10b981' : '#94a3b8' }}>{tab.sub}</span>
                  </button>
                ))}
              </div>

              {activeTab === 'prev' && (
                <PrevMonthTab prev={prevMonth} currMonthName={monthName} openGroup={openGroup} setOpenGroup={setOpenGroup} />
              )}
              {activeTab === 'current' && (
                <CurrentMonthTab report={report} prev={prevMonth} openGroup={openGroup} setOpenGroup={setOpenGroup} />
              )}
            </>
          )}
        </div>

        <div style={{ textAlign: 'center', padding: '10px 0 14px', fontSize: 11, color: '#cbd5e1' }}>
          4Sight Operations · Read-only view
        </div>
      </div>
    </div>
  );
}
